// src/Pages/Dashboard/Company/sections/commerceInit.js
//
// Admin: onboard a business user to Blitz Commerce by email. Wraps the
// admin-only backend route POST /api/commerce/initialize, which resolves the
// business user, creates their commerce integration, stands up the storefront,
// and mints the first API key (returned ONCE). Idempotent: re-initializing an
// already-onboarded user returns their integration without minting a new key.
//
// Rendered inside CompanyList (Company/index.js), which already gates the whole
// dashboard to admin accounts — no additional guard needed here.

import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useMutation } from "react-query";
import API from "../../../../API";

export default function CommerceInitView() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [feePercentage, setFeePercentage] = useState("");
  const [copied, setCopied] = useState(false);

  const {
    mutate: initialize,
    data: result,
    error,
    isLoading,
    reset,
  } = useMutation({
    mutationFn: (payload) => API.commerce.adminInitialize(payload),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { email: email.trim().toLowerCase() };
    if (name.trim()) payload.name = name.trim();
    if (feePercentage !== "") payload.fee_percentage = feePercentage;
    initialize(payload);
  };

  const apiKey = result?.api_key || null;
  const alreadyInitialized = result?.already_initialized === true;

  const copyKey = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ p: 2, maxWidth: 640, mx: "auto" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        Initialize Commerce
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Onboard a business user to Blitz Commerce by their account email. This
        creates their integration, stands up the storefront, and generates their
        first API key.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Business user email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              size="small"
              placeholder="founder@business.com"
            />
            <TextField
              label="Integration name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              placeholder="Defaults to “<Company> Commerce”"
            />
            <TextField
              label="Fee % (optional)"
              type="number"
              value={feePercentage}
              onChange={(e) => setFeePercentage(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ min: 0, max: 100, step: "0.1" }}
              helperText="Platform fee override, 0–100. Leave blank for the default."
            />
            <Box>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading || !email.trim()}
              >
                {isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Initialize commerce"
                )}
              </Button>
            </Box>
          </Stack>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error?.response?.data?.error ||
              error?.message ||
              "Failed to initialize commerce."}
          </Alert>
        )}

        {result && alreadyInitialized && (
          <Alert severity="info" sx={{ mt: 2 }} onClose={reset}>
            Commerce is already initialized for this user. Have them use the
            Developer Portal (or reroll) to issue a new key — an existing key
            can't be shown again.
          </Alert>
        )}

        {result && apiKey && (
          <Alert severity="warning" icon={false} sx={{ mt: 2, borderRadius: 2 }} onClose={reset}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
              API key — copy it now and share it securely. It won't be shown again.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {apiKey}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy"}>
                <IconButton
                  size="small"
                  onClick={copyKey}
                  color={copied ? "success" : "default"}
                >
                  <ContentCopyOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
